import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, Waves } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { GlassCard } from "@/components/GlassCard";
import { ErrorState } from "@/components/common/ErrorState";
import {
  TypeSection,
  DurationSection,
  IntensitySection,
  MoodSection,
  NoteSection,
  ShareSection,
} from "@/components/activity/FormSections";
import { useAddActivityForm } from "@/hooks/useAddActivityForm";
import { useProfile } from "@/context/ProfileContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { getActivityApi } from "@/lib/api";
import { fadeUp } from "@/lib/motion";

// Stable keys for skeleton placeholders (not array indices)
const SKELETON_KEYS = [
  "skeleton-edit-a",
  "skeleton-edit-b",
  "skeleton-edit-c",
  "skeleton-edit-d",
];

// Rendered only after the activity has loaded so the form hook can
// initialize its state from the existing values (edit mode).
const EditActivityForm = ({ activity, initialShared }) => {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { t } = useLanguage();
  const form = useAddActivityForm({ activity, initialShared });

  return (
    <>
      <TypeSection value={form.type} onChange={form.setType} delay={0.05} />

      <DurationSection
        duration={form.duration}
        customMode={form.customMode}
        customValue={form.customValue}
        onPreset={(d) => {
          form.setCustomMode(false);
          form.setDuration(d);
        }}
        onCustomMode={() => form.setCustomMode(true)}
        onCustomValue={form.setCustomValue}
        delay={0.1}
      />

      <IntensitySection value={form.intensity} onChange={form.setIntensity} delay={0.15} />

      <MoodSection
        title={t("addActivity.moodBeforeSection")}
        testPrefix="add-activity-mood-before"
        value={form.moodBefore}
        onChange={form.setMoodBefore}
        delay={0.2}
      />

      <MoodSection
        title={t("addActivity.moodAfterSection")}
        testPrefix="add-activity-mood-after"
        value={form.moodAfter}
        onChange={form.setMoodAfter}
        delay={0.25}
      />

      <NoteSection value={form.note} onChange={form.setNote} delay={0.3} />

      <ShareSection
        value={form.share}
        onChange={form.setShare}
        isPrivate={profile.privacy === "private"}
        delay={0.35}
      />

      <motion.div {...fadeUp(0.4)} className="sticky bottom-24 pt-1 space-y-2.5">
        <Button
          data-testid="edit-activity-save-button"
          onClick={form.save}
          disabled={form.saving}
          className="w-full py-6 rounded-xl bg-cyan-300 text-slate-950 text-base font-semibold hover:bg-cyan-200 active:scale-[0.98] shadow-[0_0_0_1px_rgba(34,211,238,0.25),0_12px_40px_rgba(34,211,238,0.2)] disabled:opacity-60"
        >
          {form.saving ? (
            <Loader2 className="animate-spin" size={20} />
          ) : (
            t("editActivity.save")
          )}
        </Button>
        <Button
          data-testid="edit-activity-cancel-button"
          onClick={() => navigate("/progress")}
          variant="outline"
          className="w-full py-5 rounded-xl bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white active:scale-[0.98]"
        >
          {t("editActivity.cancel")}
        </Button>
      </motion.div>
    </>
  );
};

export default function EditActivity() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error | notfound

  const load = useCallback(() => {
    setStatus("loading");
    getActivityApi(id, profile.id)
      .then((d) => {
        setData(d);
        setStatus("ready");
      })
      .catch((e) => {
        setStatus(e?.response?.status === 404 ? "notfound" : "error");
      });
  }, [id, profile.id]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4" data-testid="edit-activity-screen">
      <motion.header {...fadeUp(0)} className="pt-2 flex items-center gap-3">
        <button
          data-testid="edit-activity-back-button"
          onClick={() => navigate("/progress")}
          aria-label={t("editActivity.backAria")}
          className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:bg-white/10 hover:text-white transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("editActivity.title")}
          </h1>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            {t("editActivity.subtitle")}
          </p>
        </div>
      </motion.header>

      {status === "loading" && (
        <div className="space-y-4">
          {SKELETON_KEYS.map((key) => (
            <Skeleton key={key} className="h-24 rounded-2xl bg-white/10" />
          ))}
        </div>
      )}

      {status === "error" && (
        <ErrorState
          message={t("editActivity.loadError")}
          onRetry={load}
          testId="edit-activity-retry-button"
        />
      )}

      {status === "notfound" && (
        <GlassCard className="p-8" data-testid="edit-activity-not-found">
          <div className="flex flex-col items-center text-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-cyan-300/10 border border-cyan-300/25 flex items-center justify-center">
              <Waves size={22} className="text-cyan-300/80" />
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
              {t("editActivity.notFound")}
            </p>
            <Button
              data-testid="edit-activity-back-to-progress"
              onClick={() => navigate("/progress")}
              className="mt-1 rounded-xl bg-cyan-300 text-slate-950 font-semibold hover:bg-cyan-200 active:scale-[0.98]"
            >
              {t("editActivity.backToProgress")}
            </Button>
          </div>
        </GlassCard>
      )}

      {status === "ready" && (
        <EditActivityForm activity={data} initialShared={data.shared_to_community} />
      )}
    </div>
  );
}
